var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DndContext, DragOverlay, PointerSensor, rectIntersection, useDroppable, useSensor, useSensors, } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { FolderPlusIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "src/components/Button";
import { HelpTooltip } from "src/components/HelpTooltip";
import { useWorkspace } from "src/stores/workspace";
import { cn } from "src/utils";
import { syncItemIndices, useCreateFolder, useLibraryItems, useMakeInitialLibrary, useMoveOutOfLibrary, useSetLibraryItems, } from "src/utils/folders";
import { makeDocUrl } from "src/utils/thought";
import { useProject } from "../projects/ProjectContext";
import { CategoryHeader } from "./SidebarCategoryHeader";
import { SortableItem } from "./SortableItem";
var findDescendantFolderIds = function (items, folderId) {
    var descendants = [];
    var stack = [folderId];
    var _loop_1 = function () {
        var currentId = stack.pop();
        var children = items.filter(function (item) { return item.parentId === currentId; });
        for (var _i = 0, children_1 = children; _i < children_1.length; _i++) {
            var child = children_1[_i];
            if (child.type === "folder") {
                descendants.push(child.id);
                stack.push(child.id);
            }
        }
    };
    while (stack.length > 0) {
        _loop_1();
    }
    return descendants;
};
export var LibraryView = function () {
    var navigate = useNavigate();
    var workspace = useWorkspace();
    var project = useProject();
    var items = useLibraryItems().data;
    var setLibraryItemsMutation = useSetLibraryItems();
    var makeInitialLibraryMutation = useMakeInitialLibrary();
    var moveOutOfLibraryMutation = useMoveOutOfLibrary();
    var createFolderMutation = useCreateFolder();
    var _a = useState(new Set()), expandedFolders = _a[0], setExpandedFolders = _a[1];
    var _b = useState(null), activeId = _b[0], setActiveId = _b[1];
    var _c = useState({
        workspace: false,
        shared: false,
        private: false,
    }), expandedSections = _c[0], setExpandedSections = _c[1];
    var setItems = useCallback(function (itemsSetter) {
        var newItems = itemsSetter(items);
        setLibraryItemsMutation.mutate({ prevItems: items, newItems: newItems });
    }, [items, setLibraryItemsMutation]);
    var sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 5,
        },
    }));
    var visibleItems = items.filter(function (item) {
        if (item.parentId === "<ROOT>")
            return true;
        return item.parentId && expandedFolders.has(item.parentId);
    });
    // Separate recent items by category
    var sharedItems = items.filter(function (item) { return item.parentId === null && item.category === "shared"; });
    var privateItems = items.filter(function (item) { return item.parentId === null && item.category === "private"; });
    var workspaceItems = items.filter(function (item) { return item.parentId === null && item.category === "workspace"; });
    var handleDragStart = function (_a) {
        var active = _a.active;
        setActiveId(active.id);
    };
    var handleDragEnd = function (_a) {
        var active = _a.active, over = _a.over;
        setActiveId(null);
        if (!over)
            return;
        if (over.id === "initial") {
            makeInitialLibraryMutation.mutate(active.id);
        }
        else if (over.id === "out-of-library") {
            moveOutOfLibraryMutation.mutate(active.id);
        }
        else if (active.id !== over.id) {
            var id_1 = over.id;
            var isBefore_1 = id_1.startsWith("before:");
            var isAfter_1 = id_1.startsWith("after:");
            id_1 = id_1.includes(":") ? id_1.split(":")[1] : id_1;
            setItems(function (items) {
                var oldIndex = items.findIndex(function (item) { return item.id === active.id; });
                var newIndex = items.findIndex(function (item) { return item.id === id_1; });
                var activeItem = items.find(function (item) { return item.id === active.id; });
                if (!activeItem) {
                    throw new Error("Active item not found");
                }
                var currentItemAtNewIndex = items[newIndex];
                var newItems = items;
                var updateActiveItem = function (newItem) {
                    newItems = __spreadArray(__spreadArray(__spreadArray([], items.slice(0, oldIndex), true), [newItem], false), items.slice(oldIndex + 1), true);
                };
                var oldIndexIsBeforeNewIndex = oldIndex < newIndex;
                if (currentItemAtNewIndex.type === "folder" && !isBefore_1 && !isAfter_1) {
                    var newActiveItem = __assign(__assign({}, activeItem), { parentId: currentItemAtNewIndex.id, depth: currentItemAtNewIndex.depth + 1 });
                    updateActiveItem(newActiveItem);
                    newItems = arrayMove(newItems, oldIndex, newIndex + 1 - (oldIndexIsBeforeNewIndex ? 1 : 0));
                }
                else {
                    if (currentItemAtNewIndex.parentId) {
                        // Make sure the active item is in the same folder as the new index
                        var newActiveItem = __assign(__assign({}, activeItem), { parentId: currentItemAtNewIndex.parentId, depth: currentItemAtNewIndex.depth });
                        updateActiveItem(newActiveItem);
                    }
                    else {
                        if (activeItem.type === "folder") {
                            // Can't move a folder to recents.
                            return newItems;
                        }
                        // Moving to recents, clear any parents if there are any
                        var newActiveItem = __assign(__assign({}, activeItem), { parentId: null, depth: 0 });
                        updateActiveItem(newActiveItem);
                    }
                    newItems = arrayMove(newItems, oldIndex, (isAfter_1 ? newIndex + 1 : newIndex) - (oldIndexIsBeforeNewIndex ? 1 : 0));
                }
                newItems = syncItemIndices(newItems);
                return newItems;
            });
        }
    };
    var toggleFolder = function (folderId) {
        setExpandedFolders(function (prev) {
            var newSet = new Set(prev);
            if (newSet.has(folderId)) {
                // Closing the folder
                newSet.delete(folderId);
                // Remove all descendant folder IDs
                var descendantIds = findDescendantFolderIds(items, folderId);
                for (var _i = 0, descendantIds_1 = descendantIds; _i < descendantIds_1.length; _i++) {
                    var id = descendantIds_1[_i];
                    newSet.delete(id);
                }
            }
            else {
                // Opening the folder
                newSet.add(folderId);
            }
            return newSet;
        });
    };
    var toggleSection = function (section) {
        setExpandedSections(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[section] = !prev[section], _a)));
        });
    };
    var activeItem = activeId ? items.find(function (item) { return item.id === activeId; }) : null;
    // Add the navigateToDocument callback near other hooks
    var navigateToDocument = useCallback(function (item) {
        if (item.type !== "folder") {
            navigate(makeDocUrl({
                workspaceSlug: workspace.slug,
                projectSlug: project === null || project === void 0 ? void 0 : project.slug,
                docId: item.id,
            }));
        }
    }, [navigate, workspace.slug, project === null || project === void 0 ? void 0 : project.slug]);
    return (_jsx("div", { className: "flex w-full flex-col", children: _jsxs(DndContext, { sensors: sensors, collisionDetection: rectIntersection, onDragStart: handleDragStart, onDragEnd: handleDragEnd, children: [_jsx(SortableContext, { items: items.map(function (item) { return item.id; }), strategy: verticalListSortingStrategy, children: _jsxs("div", { className: "flex w-full flex-col gap-1", children: [_jsxs("div", { className: "flex flex-row items-center justify-between gap-1", children: [_jsx(CategoryHeader, { title: "Library", children: _jsx(HelpTooltip, { content: "Drag files and folders around to build this ".concat((project === null || project === void 0 ? void 0 : project.name) ? "project's" : "workspace's", " library. This is where you can structure your knowledgebase.") }) }), _jsx(Button, { variant: "ghost", size: "icon-sm", className: "text-secondary", onClick: function () { return createFolderMutation.mutate(); }, children: _jsx(FolderPlusIcon, { className: "size-4" }) })] }), visibleItems.length > 0 ? (_jsx("ul", { className: "flex flex-col gap-1", children: visibleItems.map(function (item, index, arr) {
                                    var nextItem = arr[index + 1];
                                    var isLastItemWithParent = Boolean(item.parentId && (nextItem === null || nextItem === void 0 ? void 0 : nextItem.parentId) !== item.parentId);
                                    return (_jsx(SortableItem, { item: item, expanded: expandedFolders.has(item.id), toggleFolder: toggleFolder, navigateToDoc: function () { return navigateToDocument(item); }, hasAfterDroppable: index === arr.length - 1 || isLastItemWithParent, isInLibrary: true }, item.id));
                                }) })) : (_jsx(EmptyLibraryDroppable, {})), _jsx(MoveOutOfLibraryDroppable, { hasRecentDocs: sharedItems.length > 0 || privateItems.length > 0 || workspaceItems.length > 0 }), workspaceItems.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx(CategoryHeader, { title: "Recent" }), workspaceItems.length > 5 && (_jsx(Button, { variant: "ghost", size: "xs", className: "text-xs text-tertiary", onClick: function () { return toggleSection("workspace"); }, children: expandedSections.workspace ? "Show less" : "+".concat(workspaceItems.length - 5, " more") }))] }), _jsx("ul", { className: "flex flex-col gap-1", children: workspaceItems
                                            .slice(0, expandedSections.workspace ? undefined : 5)
                                            .map(function (item, index, arr) { return (_jsx(SortableItem, { item: item, expanded: false, toggleFolder: toggleFolder, navigateToDoc: function () { return navigateToDocument(item); }, hasAfterDroppable: index === arr.length - 1 }, item.id)); }) })] })), sharedItems.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx(CategoryHeader, { title: "Shared With You" }), sharedItems.length > 5 && (_jsx(Button, { variant: "ghost", size: "xs", className: "text-xs text-tertiary", onClick: function () { return toggleSection("shared"); }, children: expandedSections.shared ? "Show less" : "+".concat(sharedItems.length - 5, " more") }))] }), _jsx("ul", { className: "flex flex-col gap-1", children: sharedItems.slice(0, expandedSections.shared ? undefined : 5).map(function (item, index, arr) { return (_jsx(SortableItem, { item: item, expanded: false, toggleFolder: toggleFolder, navigateToDoc: function () { return navigateToDocument(item); }, hasAfterDroppable: index === arr.length - 1 }, item.id)); }) })] })), privateItems.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx(CategoryHeader, { title: "Private" }), privateItems.length > 5 && (_jsx(Button, { variant: "ghost", size: "xs", className: "text-xs text-tertiary", onClick: function () { return toggleSection("private"); }, children: expandedSections.private ? "Show less" : "+".concat(privateItems.length - 5, " more") }))] }), _jsx("ul", { className: "flex flex-col gap-1", children: privateItems.slice(0, expandedSections.private ? undefined : 5).map(function (item, index, arr) { return (_jsx(SortableItem, { item: item, expanded: false, toggleFolder: toggleFolder, navigateToDoc: function () { return navigateToDocument(item); }, hasAfterDroppable: index === arr.length - 1 }, item.id)); }) })] }))] }) }), _jsx(DragOverlay, { children: activeItem ? (_jsx("div", { className: "opacity-40 shadow-md", children: _jsx(SortableItem, { item: activeItem, expanded: false, toggleFolder: function () { }, isOverlay: true }) })) : null })] }) }));
};
var EmptyLibraryDroppable = function () {
    var _a = useDroppable({
        id: "initial",
    }), isOver = _a.isOver, setNodeRef = _a.setNodeRef;
    return (_jsx("div", { ref: setNodeRef, className: cn("my-1 w-full rounded border border-dashed border-border p-2 text-center text-xs text-tertiary", isOver && "bg-card text-accent"), children: "Move a doc here to start building your library" }));
};
var MoveOutOfLibraryDroppable = function (_a) {
    var _b;
    var hasRecentDocs = _a.hasRecentDocs;
    var _c = useDroppable({
        id: "out-of-library",
    }), isOver = _c.isOver, setNodeRef = _c.setNodeRef, active = _c.active;
    if (!active || !((_b = active.data.current) === null || _b === void 0 ? void 0 : _b.isInLibrary) || active.data.current.type === "folder") {
        return null;
    }
    return (_jsx("div", { ref: setNodeRef, className: cn("my-1 w-full rounded border border-dashed border-border p-2 text-center text-xs text-tertiary", isOver && "bg-card text-accent"), children: "Move out of library" }));
};
